/**
 * Serviço de Organização
 * Responsável por gerenciar informações da organização
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8090';

export interface OrganizationStatus {
  organization_id: string;
  organization_name: string;
  logo_url?: string;  // Logo da organização
  is_operating: boolean;
  current_time: string;
  timezone: string;
  operating_hours: {
    start: string;
    end: string;
    days: number[];
  };
}

class OrganizationService {
  /**
   * Busca o status da organização pelo ID
   */
  async getOrganizationStatus(organizationId: string, token?: string): Promise<OrganizationStatus> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Se tiver token, adiciona no header
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Enviar organização ativa para usuários de grupo
    const activeOrgId = localStorage.getItem('awflux_active_organization_id');
    if (activeOrgId) {
      headers['X-Organization-Id'] = activeOrgId;
    }

    const response = await fetch(
      `${API_BASE_URL}/api/v1/organizations/${organizationId}/status`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        detail: 'Erro ao buscar status da organização' 
      }));
      throw new Error(error.detail || 'Erro ao buscar organização');
    }

    return await response.json();
  }

  /**
   * Busca o nome da organização pelo ID (simplificado)
   */
  async getOrganizationName(organizationId: string, token?: string): Promise<string> {
    try {
      const status = await this.getOrganizationStatus(organizationId, token);
      return status.organization_name;
    } catch (error) {
      console.error('Erro ao buscar nome da organização:', error);
      return 'AWFlux'; // Fallback para o nome padrão
    }
  }

  /**
   * Busca dados completos da organização (para AuthContext)
   */
  async getOrganizationDetails(organizationId: string, token?: string): Promise<{ id: string; name: string; logo_url?: string }> {
    try {
      const status = await this.getOrganizationStatus(organizationId, token);
      return {
        id: status.organization_id,
        name: status.organization_name,
        logo_url: status.logo_url,
      };
    } catch (error) {
      console.error('Erro ao buscar detalhes da organização:', error);
      return {
        id: organizationId,
        name: 'AWFlux',
        logo_url: undefined,
      };
    }
  }
}

export const organizationService = new OrganizationService();
